import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PRODUCT_IMAGE, heroGuarantees } from '../data/drum';
import { PAGE_COVER, PAGE_LESSON7, PAGE_YEARPLAN } from '../data/pageSamples';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Hero() {
  return <section className="relative w-full overflow-hidden bg-seyes" data-id="element-154" data-mp="4na0kq3ifu6v">
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-4 w-px bg-red-300 sm:left-8 lg:left-12" data-id="element-155" data-mp="129qvjoortvpg" />

      <div className="relative mx-auto max-w-6xl px-8 pb-16 pt-4 sm:px-14 sm:pb-20 sm:pt-6 lg:pb-28" data-id="element-156" data-mp="xaxejma9zmkj">
        

        <div className="mt-4 flex flex-col sm:mt-12 lg:mt-16 lg:grid lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-12" data-id="element-157" data-mp="s9gji7eh2ubv">
          <div className="contents lg:block" data-id="element-158" data-mp="3d1ilrd3pdip0">
            <motion.h1 initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: EASE
          }} className="order-1 text-center font-serif text-[2.15rem] leading-none sm:leading-[0.95] lg:text-left tracking-tight text-slate-900 sm:text-6xl lg:text-[4rem]" data-id="element-159" data-mp="3d2bidl8y6rtf">
              Get your{' '}
              <span className="relative inline-block whitespace-nowrap" data-id="element-160" data-mp="39a38f34q1jod">
                <motion.span aria-hidden="true" initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 0.3,
                delay: 0.25,
                ease: EASE
              }} className="absolute inset-x-[-0.08em] bottom-[0.08em] top-[0.38em] -z-0 origin-left -rotate-1 rounded-[0.12em] bg-amber-300/80" data-id="element-161" data-mp="3e84orgarj4w5" />
                <span className="relative" data-id="element-162" data-mp="2vjpzaxg3fyxg">whole studio year</span>
              </span>{' '}
              of drum lessons planned in just{' '}
              <span className="relative inline-block whitespace-nowrap italic text-red-700" data-id="element-163" data-mp="2lkqzm4db3dml">
                5 minutes
                <svg aria-hidden="true" viewBox="0 0 200 14" preserveAspectRatio="none" className="absolute -bottom-1.5 left-0 h-3 w-full sm:-bottom-2.5" data-id="element-164" data-mp="s2jmbumnjwig">
                  <motion.path d="M3 9 C 45 3, 95 2, 135 6 S 185 11, 197 4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke" initial={{
                  pathLength: 0
                }} animate={{
                  pathLength: 1
                }} transition={{
                  duration: 0.3,
                  delay: 0.35,
                  ease: EASE
                }} data-id="element-165" data-mp="na06er0hspah" />
                </svg>
              </span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.08,
            ease: EASE
          }} className="order-3 mx-auto mt-4 max-w-xl text-center text-[15px] lg:mx-0 lg:text-left leading-snug text-slate-900/75 sm:mt-8 sm:text-xl sm:leading-relaxed" data-id="element-166" data-mp="110wqtuk9m06g">
              Stop planning from scratch. Six digital volumes for drum teachers, ages 6 to adult: a studio
              year plan, ready-to-teach lessons, rudiments, 100+ original grooves, worksheets in 3 levels with answer keys and progress reports – plus five bonuses.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.16,
            ease: EASE
          }} className="order-4 mt-5 sm:mt-8 lg:mt-10" data-id="element-167" data-mp="2b2snmidlg810">
              <div className="flex justify-center lg:justify-start" data-id="element-168" data-mp="2oyebunc06nsd">
                <CtaButton href="#offer-card" className="w-full sm:w-auto" data-id="element-169" data-mp="2tz1xethgqcf7" />
              </div>
              <ul className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-xs text-slate-900/70 sm:mt-6 sm:gap-x-6 sm:text-sm lg:justify-start" data-id="element-170" data-mp="unrbgzlzxpug">
                {heroGuarantees.map(item => <li key={item} className="flex items-center gap-2" data-id="element-171" data-mp="4acnpgwqpx9j">
                    <CheckIcon className="h-4 w-4 text-red-700" aria-hidden="true" data-id="element-172" data-mp="3vhxg7je3l7yj" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>

          <motion.figure initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3,
          delay: 0.12,
          ease: EASE
        }} className="relative order-2 mx-auto mt-6 w-full max-w-[21rem] sm:mt-12 sm:max-w-md lg:mt-0" data-id="element-173" data-mp="9ah1v0a6efwl">
            <a href="#inside-pages" className="group relative block aspect-square w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-red-700/30" aria-label="Look inside Volume 1: real pages" data-id="element-174" data-mp="3rgvhr4s08zxm">
              <img src={PAGE_YEARPLAN} alt="Volume 1, page 5: 36-week plan for young beginners" className="absolute left-[1%] top-[9%] w-[50%] -rotate-[8deg] rounded-md border border-slate-900/10 bg-white shadow-xl shadow-slate-900/20" data-id="element-175" data-mp="2yy805zc7w31a" />
              <img src={PAGE_LESSON7} alt="Volume 1, page 24: Lesson 7 First Fills with 1 e & a counting charts" className="absolute right-[1%] top-[9%] w-[50%] rotate-[7deg] rounded-md border border-slate-900/10 bg-white shadow-xl shadow-slate-900/20" data-id="element-176" data-mp="1e9dorh1luxvd" />
              <img src={PAGE_COVER} alt="Volume 1 cover: Studio Year Plan & 12 Ready-to-Teach Lessons" className="absolute left-1/2 top-0 w-[56%] -translate-x-1/2 rounded-md border border-slate-900/10 bg-white shadow-2xl shadow-slate-900/30 transition-transform duration-200 ease-out group-hover:-translate-y-1" data-id="element-177" data-mp="14kcb3xij8skk" />
              <img src={PRODUCT_IMAGE} alt="The DRUM READY package: six volumes for drum teachers" className="absolute -bottom-[3%] -right-[3%] w-[40%] drop-shadow-2xl" data-id="element-178" data-mp="1j873fcsmoux4" />
            </a>
            <figcaption className="mt-3 text-center text-sm text-slate-900/70" data-id="element-179" data-mp="7sidcck2vfs3">
              <a href="#inside-pages" className="font-semibold text-red-700 underline-offset-4 hover:underline" data-id="element-180" data-mp="bvyb5c7vvm6d">Real pages from Volume 1</a>
              <span data-id="element-181" data-mp="2ejn43qs06aeq"> · 6 volumes + 5 bonuses</span>
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>;
}